<template>
  <div class="flex flex-col h-[calc(100vh-52px)] relative min-h-0 overflow-y-auto">
    <div
      v-if="isSidepaneLoading"
      class="absolute inset-0 z-50 flex items-center justify-center bg-white bg-opacity-75 rounded-lg"
    >
      <div class="flex flex-col items-center gap-3">
        <div class="w-12 h-12 border-b-2 rounded-full animate-spin border-primary"></div>
        <span class="font-medium text-gray-600">Updating order...</span>
      </div>
    </div>
    <div class="fixed top-[60px] right-5 z-[10002] flex items-center gap-2 bg-[#f3f3f3]">
      <span
        @click="emits('onClose')"
        class="flex justify-center items-center p-2 text-white bg-gray-400 rounded-full cursor-pointer hover:bg-gray-600"
      >
        <XMarkIcon class="text-white size-5" />
      </span>
    </div>

    <div ref="headerContainer" class="sticky top-0 z-[10001] pt-4 bg-[#f3f3f3]">
      <div class="pl-5 pr-8 border-b pb-[11px] mb-2 flex justify-between items-center gap-2 flex-wrap">
        <div class="flex items-center gap-2">
          <span v-if="selectedOrderDetails?.order_no">
            <span class="font-bold text-md">ORDER# {{ selectedOrderDetails?.order_no }}</span>
          </span>
          <span class="font-bold text-md" v-else> Order pending confirmation</span>
          &nbsp;
        <span class="text-md font-light capitalize">{{ cleanName(selectedOrderDetails?.customer_name) ? selectedOrderDetails?.customer_name : "N/A" }}</span>        
        <!-- Customer Reference Number -->
        <div class="flex text-md px-2" v-if="!isNullOrEmpty(customerReferenceNo)">
          <span class="font-bold">Reference:</span>
          <span class="pl-1 text-md">{{ customerReferenceNo }}</span>
        </div>

        <!-- Factory PO Number -->
        <div class="flex text-md items-center gap-1">
          <span class="font-bold">PO Num:</span>
          <span class="pl-1">{{ factoryPoNumber }}</span>
          
          <!-- PO File Download Link -->
          <template v-if="poFileInfo">
            <span class="font-bold pl-2">PO Document:</span>
            <a
              :href="poFileDownloadUrl"
              :download="poFileName"
              class="ml-2 text-blue-600 hover:text-blue-800 cursor-pointer flex items-center gap-1"
              :title="`Download PO: ${poFileName}`"
            >
              <span class="text-sm">Open File</span>
            </a>
          </template>
        </div>
        </div>
        <div
          class="mr-6 flex flex-col"
          v-if="
            (can('display-csManager') && selectedOrderDetails?.cs_manager) ||
            (can('display-salesRepresentative') && selectedOrderDetails?.sales_representative)
          "
        >
          <span
            v-if="can('display-csManager') && selectedOrderDetails?.cs_manager"
            class="text-md"
          >
            <strong>CSM: </strong>{{ selectedOrderDetails.cs_manager?.name }}
          </span>
        
          <span
            v-if="can('display-salesRepresentative') && selectedOrderDetails?.sales_representative"
            class="text-md"
          >
            <strong>SR: </strong>{{ selectedOrderDetails.sales_representative?.name }}
          </span>
        </div>

        </div>
      <div
       ref="selectContainer"
        class="absolute left-5 flex items-center gap-2 bg-[#f3f3f3] w-full md:w-max py-2 md:py-0"
        v-if="selectedRow?.factory_id"
      >
        <Select
          v-if="can('assign-order-administrator') && selectedRow?.factory_id"
          :options="
            orderAdmins.filter((admin: OrderAdminUser) => {
              // If admin has 'leadorderAdministrator' role, include them without factory_id filtering
              // Format role names by removing spaces for comparison
              const formattedRoleNames = admin.role_names?.map(role => 
                role.replace(/\s+/g, '')
              );
              if (formattedRoleNames?.some(role => role.toLowerCase() === 'leadorderadministrator')) {
                return true;
              }
              // Otherwise, filter by factory_id
              return admin.factory_ids?.includes((selectedRow as DashboardItem).factory_id);
            })
          "
          :modelValue="selectedRow.order.order_administrator"
          @update:modelValue="onAssignAdmin"
          track-by="id"
          label="name"
          classProps="assign-admin-button min-w-[160px] max-w-max"
          placeholder="Assign Admin"
          append-to-body
        />
      </div>
    </div>
    <Tabs
      v-if="selectedOrderDetails"
      :tabs="filteredOrderDetailTabs"
      :initial-tab="initialTab"
      :key="selectedRow.id"
      @change="(tabId: string) => emits('detailsTabChange', tabId)"
      sticky
      sticky-class="sticky z-[10000] bg-[#f3f3f3] overflow-hidden dynamic-sticky-tabs"
    >
      <template #actions>
        <div class="flex justify-center items-center px-4 border-b mt-10 md:mt-0  overflow-y-auto">
          <OrderDetailActions
            :current-tab="currentTab"
            :order_item_list="selectedRow"
            :order_item_price="selectedRow.order?.price_info?.order_final_price"
            :order="selectedOrderDetails"
            @onFetch="() => emits('fetchOnSave', true)"
            @handleOrderUpdated="() => emits('handleOrderUpdated')"
            @openOrderPriceModal="() => emits('openOrderPriceModal')"
          />
        </div>
      </template>
      <template #timeline>
        <div class="p-5 overflow-y-auto mt-5 md:mt-0">
          <OrderTimeline
            :order="selectedOrderDetails"
            :order_item="currentOrderItem"
            :order_item_index="currentOrderItemIndex"
            :order_no="selectedOrderDetails.order_no"
            :isLoading="isSidepaneLoading"
            :current-tab="currentTab"
            :selectedRow="selectedRow"
            @onFetch="() => emits('fetchOnSave', true)"
            @orderMoved="emits('handleOrderMoved')"
            @orderUpdated="emits('handleOrderUpdated')"
          />
        </div>
      </template>
      <template #detail>
        <div class="p-5 overflow-y-auto mt-10 md:mt-0">
          <FactoryProductsTab
            v-if="currentOrderItem?.factory_products?.length"
            :factoryProducts="JSON.parse(JSON.stringify(currentOrderItem?.factory_products))"
            :order="selectedOrderDetails"
            :order_item_id="currentOrderItem?.id"
            :order_item_price="selectedRow.price_info?.order_item_final_price"
            @openOrderPriceModal="() => emits('openOrderPriceModal')"
            @handleOrderUpdated="() => emits('handleOrderUpdated')"
            :key="JSON.stringify(currentOrderItem?.factory_products)"
              :copiedRoster="copiedRoster"
              @onCopyRoster="copyRoster"
            />
          <p class="text-gray-700" v-else>No factory products available.</p>
        </div>
      </template>
      <template #files>
        <div class="p-5 overflow-y-auto mt-10 md:mt-0">
          <div v-if="currentOrderItem?.factory_id" class="p-5 border border-gray-200 rounded-lg">
            <h2 class="text-xl font-semibold mb-3">Files Content</h2>
            <OrderFiles
              :order="currentOrderItem"
              :order-id="currentOrderItem.order_id"
              :order-factories="[
                {
                  id: currentOrderItem.factory_id,
                  name: currentOrderItem.factory_name,
                },
              ]"
                @show-logs="showFileLogs"
                @loading-logs="toggleFileLogsLoading"
            />
          </div>
          <div
            v-else
            class="p-5 border border-gray-200 rounded-lg flex items-center w-full justify-center bg-white"
          >
            <EmptyState title="Oops!" message="Please assign a factory to view files.." />
          </div>
            <SharePointActivityLogs
              :showActivitiesPanel="sharepointLogsVisible"
              :isSharepointLogsLoading="isSharepointLogsLoading"
              :sharePointActivityLogsObject="sharepointActivityLogsObject"
              @close-sharepoint-activity-panel="closeSharepointActivityLog">
            </SharePointActivityLogs>
          </div>
      </template>
    </Tabs>
  </div>

</template>
<script setup lang="ts">
import FactoryProductsTab from '@/components/Dashboard/Order/components/FactoryProductsTab.vue';
import OrderTimeline from '@/components/Dashboard/Order/OrderTimeline.vue';
import EmptyState from '@/components/Shared/EmptyState.vue';
import Select from '@/components/Shared/Select.vue';
import Tabs from '@/components/Shared/Tabs.vue';
import { can, cleanName, isNullOrEmpty } from '@/composables';
  import { DashboardItem, OrderAdminUser, OrderDetail, OrderDetailItem } from '@/models';
import { CopiedRoster } from '@/models/CopiedRoster';
import { SharepointActivityLogsObject } from '@/models/SharepointActivityLog';
import { useMainStore } from '@/store';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import OrderDetailActions from './components/OrderDetailActions.vue';
import SharePointActivityLogs from './components/SharePointActivityLogs.vue';
import OrderFiles from './OrderFiles.vue';
import { XMarkIcon } from '@heroicons/vue/24/outline';


interface Tab {
  id: string;
  label: string;
}
const { updateOrderAdmin } = useMainStore();
interface Props {
  currentTab?: string;
  selectedOrderDetails: OrderDetail;
  isSidepaneLoading: boolean;
  selectedRow: DashboardItem;
  filteredOrderDetailTabs: Tab[];
  currentOrderItem: OrderDetailItem | null;
  currentOrderItemIndex: number | null;
  sidebarOpen: boolean;
  initialTab: string;
  orderAdmins: OrderAdminUser[];
}

const props = defineProps<Props>();
const emits = defineEmits([
  'fetchOnSave',
  'handleOrderMoved',
  'handleOrderUpdated',
  'openOrderPriceModal',
  'detailsTabChange',
  'onClose',
]);
const selectContainer = ref<HTMLElement | null>(null);
const headerContainer = ref<HTMLElement | null>(null);

// Computed properties for header fields
const customerReferenceNo = computed(() => {
  return props.selectedOrderDetails?.customer_reference_no || '';
});

const factoryPoNumber = computed(() => {
  return props.selectedOrderDetails?.additional_fields?.po_number || 'N/A';
});

const poFileInfo = computed(() => {
  return props.selectedOrderDetails?.additional_fields?.po_file_info;
});

const poFileDownloadUrl = computed(() => {
  if (!poFileInfo.value) return '';
  const BASE_URL = import.meta.env.VITE_APP_FILE_URL; 
  return `${BASE_URL}/${poFileInfo.value.path}`;
});

const poFileName = computed(() => {
  if (!poFileInfo.value) return '';
  return poFileInfo.value.name || poFileInfo.value.filename || 'PO_Document';
});
const copiedRoster = ref<CopiedRoster>({
    isAnyRosterCopied: false,
    copiedFromProductIndex: null,
    rosterData: []
});
const sharepointActivityLogsObject = ref<SharepointActivityLogsObject>(null)
const sharepointLogsVisible = ref<Boolean>(false);
const isSharepointLogsLoading = ref(false);

const onAssignAdmin = async (newValue: OrderAdminUser) => {
  const payload = {
    user_id: newValue ? newValue.id : null,
    order_ids: props.selectedRow.order.id,
  };
  await updateOrderAdmin(payload);
  emits('fetchOnSave', true);
};

const copyRoster = (data) => {
  copiedRoster.value.isAnyRosterCopied = data.copiedRoster.isAnyRosterCopied;
  copiedRoster.value.copiedFromProductIndex = data.copiedRoster.copiedFromProductIndex;
  copiedRoster.value.rosterData = data.copiedRoster.rosterData;
}
const showFileLogs = async (fileLogsResult: SharepointActivityLogsObject) => {
  sharepointActivityLogsObject.value = fileLogsResult
}

const toggleFileLogsLoading = () => {
  sharepointLogsVisible.value = true;
  isSharepointLogsLoading.value = !isSharepointLogsLoading.value;
}

const closeSharepointActivityLog = () => {
  sharepointLogsVisible.value = false;
}

const getFileName = (action) => {
  const key = Object.keys(action)[0];
  const obj = action[key];
  // pick correct filename field
  return obj.oldName || obj.name || "N/A";
}
const formatDate = (date) => {
  return new Date(date).toLocaleString();
}
const getActionType = (action) =>{
  // returns the key (rename, delete, etc.)
  return Object.keys(action)[0];
}
onMounted(async () => {
  await nextTick();
  updateStickyOffset();
  window.addEventListener("resize", updateStickyOffset);
});
watch(
  () => props.selectedRow?.factory_id,
  async (factoryId) => {
    if (!factoryId) return;

    await nextTick();
    updateStickyOffset();
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateStickyOffset);
});

const updateStickyOffset = () => {
  const select = selectContainer.value;
  const header = headerContainer.value;
  if (header && select && window.innerWidth < 768) {
    document.documentElement.style.setProperty(
      "--dynamic-header-height",
      `${header.offsetHeight + select.offsetHeight -5}px`
    );
  } else {
    document.documentElement.style.setProperty("--dynamic-header-height", `${header.offsetHeight}px`);
  }
};
</script>
<style>
.dynamic-sticky-tabs {
  top: var(--dynamic-header-height);
}
</style>
